import React, {useCallback, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box, Flex} from '@salesforce/retail-react-app/app/components/shared/ui';
import {hasItems} from '@amblique/king-living/app/utils/utils';

import {MegaMenuTrigger} from '@amblique/king-living/app/components/mega-menu/desktop/mega-menu-trigger';

const CLOSE_DELAY_MS = 100;

export const MegaMenuDesktop = ({menuItems = [], navigationMenu, ...props}) => {
  const [activeMenuId, setActiveMenuId] = useState(null);
  const closeTimeoutRef = useRef(null);
  const isKeyboardOpenRef = useRef(false);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMenuOpen = useCallback((menuId) => {
    clearCloseTimeout();

    setActiveMenuId((current) => {
      // If opening a different menu item, reset keyboard flag
      if (current !== menuId) {
        isKeyboardOpenRef.current = false;
      }

      return menuId;
    });
  }, [clearCloseTimeout]);

  const handleMenuClose = useCallback(() => {
    // Don't close if opened via keyboard (unless explicitly called)
    if (isKeyboardOpenRef.current) {
      return;
    }
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      isKeyboardOpenRef.current = false; // Reset flag when closing
      setActiveMenuId(null);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  // Force close (for Escape key or explicit close)
  const handleMenuForceClose = useCallback(() => {
    clearCloseTimeout();
    isKeyboardOpenRef.current = false; // Reset flag
    setActiveMenuId(null);
  }, [clearCloseTimeout]);

  // Toggle menu open/close (for keyboard navigation)
  const handleMenuToggle = useCallback((menuId) => {
    clearCloseTimeout();

    setActiveMenuId((current) => {
      const willBeOpen = current !== menuId;

      isKeyboardOpenRef.current = willBeOpen; // Set flag if opening via keyboard

      return current === menuId ? null : menuId;
    });
  }, [clearCloseTimeout]);

  // Stable handlers that read menuId from data attribute
  const handleMenuOpenById = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId;

    if (menuId) {
      handleMenuOpen(menuId);
    }
  }, [handleMenuOpen]);

  const handleMenuToggleById = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId;

    if (menuId) {
      handleMenuToggle(menuId);
    }
  }, [handleMenuToggle]);

  if (!hasItems(menuItems)) {
    return null;
  }

  return (
    <Box position="relative" {...props}>
      <Flex
        alignItems="center"
        as="nav"
        gap={4}
        onMouseLeave={handleMenuClose}
      >
        {menuItems.map((menuItem) => (
          <MegaMenuTrigger
            key={menuItem.sys?.id}
            isOpen={activeMenuId === menuItem.sys?.id}
            menuId={menuItem.sys?.id}
            menuItem={menuItem}
            navigationMenu={navigationMenu}
            onClose={handleMenuClose}
            onForceClose={handleMenuForceClose}
            onOpen={handleMenuOpenById}
            onToggle={handleMenuToggleById}
          />
        ))}
      </Flex>
    </Box>
  );
};

MegaMenuDesktop.displayName = 'MegaMenuDesktop';
MegaMenuDesktop.propTypes = {
  menuItems: PropTypes.array,
  navigationMenu: PropTypes.object,
};
